import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi, type Track as TrackType, type Course } from '../hooks/useApi';
import CourseCard from '../components/CourseCard';
import { Loader2, ArrowLeft, BookOpen, Beaker, Sparkles } from 'lucide-react';

interface TrackData {
  track: TrackType;
  courses: Course[];
}

export default function Track() {
  const { slug } = useParams<{ slug: string }>();
  const { fetchApi } = useApi();
  const [data, setData] = useState<TrackData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTrack();
  }, [slug]);

  async function loadTrack() {
    setLoading(true);
    try {
      const result = await fetchApi<TrackData>(`/api/tracks/${slug}`);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load track');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Track not found'}</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-2 text-accent-blue hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to tracks
        </Link>
      </div>
    );
  }

  const { track, courses } = data;
  const levelLabel = track.level === 1 ? 'Core' : track.level === 2 ? 'Lab' : 'Advanced';
  const Icon = track.level === 1 ? BookOpen : track.level === 2 ? Beaker : Sparkles;
  const levelColor = track.level === 1
    ? 'text-accent-blue'
    : track.level === 2
    ? 'text-accent-purple'
    : 'text-accent-orange';

  return (
    <div>
      {/* Breadcrumb */}
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        All Tracks
      </Link>

      {/* Track Header */}
      <div className="mb-8">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800 ${levelColor} text-sm mb-4`}>
          <Icon className="w-4 h-4" />
          {levelLabel} Track
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">
          {track.title}
        </h1>
        <p className="text-lg text-gray-400 max-w-3xl">
          {track.description}
        </p>
      </div>

      {/* Courses */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Courses ({courses.length})
        </h2>
        {courses.length === 0 ? (
          <div className="bg-background-secondary rounded-xl p-8 text-center border border-gray-800">
            <p className="text-gray-400">No courses available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {courses.map(course => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
